/**
 * Presentational component for a wrapped grid of selectable role chips
 *
 * Renders role options as pressable chips in a flex-wrap row layout.
 * Shows a "Show more" / "Show less" toggle button when options exceed
 * the visible limit. Selected chips use a distinct border and text color.
 * Matches the Figma filter panel chip convention.
 * @module features/scouting/components/RoleChipGrid/RoleChipGridView
 */

import React, { memo } from "react";
import { Platform, type ViewStyle } from "react-native";

import { Stack } from "@/components/atoms/Stack";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/** A single selectable role option */
export interface RoleOption {
  readonly id: string;
  readonly label: string;
}

/** Props for RoleChipGridView */
interface RoleChipGridViewProps {
  /** Pre-sliced options to display (collapsed or full list) */
  readonly visibleOptions: readonly RoleOption[];
  /** Currently selected role IDs */
  readonly selectedIds: readonly string[];
  /** Pre-built toggle handlers keyed by option ID */
  readonly optionHandlers: Record<string, () => void>;
  /** Whether the full list is expanded */
  readonly isExpanded: boolean;
  /** Toggle expanded state */
  readonly onToggleExpanded: () => void;
  /** Whether there are more items than currently visible */
  readonly hasMore: boolean;
  /** Whether dark mode is active — needed for inline styles that NativeWind dark: can't reach */
  readonly isDark: boolean;
  /** ID of the currently hovered chip (web only) */
  readonly hoveredId: string | null;
  /** Called when a chip is hovered */
  readonly onHoverIn: (id: string) => void;
  /** Called when a chip hover ends */
  readonly onHoverOut: () => void;
}

/** Unselected chip colors keyed by theme */
const UNSELECTED_COLORS = {
  dark: { borderColor: "#48484A", backgroundColor: "#2C2C2E" },
  light: { borderColor: "#EDEDED", backgroundColor: "#F6F6F6" },
} as const;

/** Unselected chip hover colors keyed by theme */
const UNSELECTED_HOVER_COLORS = {
  dark: { borderColor: "#636366", backgroundColor: "#3A3A3C" },
  light: { borderColor: "#D1D1D6", backgroundColor: "#EBEBEB" },
} as const;

/** Selected chip colors keyed by theme */
const SELECTED_COLORS = {
  dark: {
    borderColor: "#0A84FF",
    backgroundColor: "rgba(10, 132, 255, 0.2)",
  },
  light: {
    borderColor: "#007AFF",
    backgroundColor: "rgba(0, 122, 255, 0.15)",
  },
} as const;

/** Selected chip hover colors keyed by theme */
const SELECTED_HOVER_COLORS = {
  dark: {
    borderColor: "#0A84FF",
    backgroundColor: "rgba(10, 132, 255, 0.3)",
  },
  light: {
    borderColor: "#007AFF",
    backgroundColor: "rgba(0, 122, 255, 0.25)",
  },
} as const;

/**
 * Returns the chip border/background colors based on selection and hover state
 * @param isSelected - Whether the chip is selected
 * @param isHovered - Whether the chip is hovered
 * @param isDark - Whether dark mode is active
 */
const getChipColors = (
  isSelected: boolean,
  isHovered: boolean,
  isDark: boolean
): { borderColor: string; backgroundColor: string } => {
  const theme = isDark ? "dark" : "light";
  if (isSelected) {
    return isHovered ? SELECTED_HOVER_COLORS[theme] : SELECTED_COLORS[theme];
  }
  return isHovered ? UNSELECTED_HOVER_COLORS[theme] : UNSELECTED_COLORS[theme];
};

const isWeb = Platform.OS === "web";

/**
 * Runtime-only chip container style: the data-driven selection/hover/isDark
 * border + background colors (translucent rgba, no semantic token) plus the
 * off-ladder 48% flex basis. All static layout lives on the chip className.
 * @param chipColors - Theme/selection-driven background + border colors
 */
const chipStyle = (
  chipColors: ReturnType<typeof getChipColors>
): ViewStyle => ({
  flexBasis: "48%",
  flexGrow: 1,
  flexShrink: 0,
  cursor: isWeb ? "pointer" : undefined,
  ...chipColors,
});

/**
 * Renders a grid of role chips with "Show more" / "Show less" toggle
 * @param root0 - Component props
 * @param root0.visibleOptions - Pre-sliced role options to display
 * @param root0.selectedIds - Selected role IDs
 * @param root0.optionHandlers - Pre-built toggle handlers
 * @param root0.isExpanded - Whether all roles are shown
 * @param root0.onToggleExpanded - Toggle expand/collapse
 * @param root0.hasMore - Whether more items exist beyond visible
 * @param root0.isDark - Whether dark mode is active
 * @param root0.hoveredId - ID of the currently hovered chip
 * @param root0.onHoverIn - Hover start handler
 * @param root0.onHoverOut - Hover end handler
 */
const RoleChipGridView = ({
  visibleOptions,
  selectedIds,
  optionHandlers,
  isExpanded,
  onToggleExpanded,
  hasMore,
  isDark,
  hoveredId,
  onHoverIn,
  onHoverOut,
}: RoleChipGridViewProps) => (
  <Stack space="1">
    <Stack direction="horizontal" space="1" className="flex-row flex-wrap">
      {visibleOptions.map(option => {
        const isSelected = selectedIds.includes(option.id);
        const isHovered = hoveredId === option.id;
        const chipColors = getChipColors(isSelected, isHovered, isDark);

        return (
          <Pressable
            key={option.id}
            onPress={optionHandlers[option.id]}
            {...(isWeb
              ? {
                  onHoverIn: () => onHoverIn(option.id),
                  onHoverOut,
                }
              : {})}
            className="web:transition-colors web:duration-150 h-9 items-center justify-center rounded-none border-2 border-solid px-2"
            // eslint-disable-next-line no-restricted-syntax -- data-driven chip border/background colors (translucent rgba, no semantic token) + off-ladder 48% flex basis
            UNSAFE_style={chipStyle(chipColors)}
          >
            <Text
              variant="unstyled"
              className={`text-center font-medium text-caption ${
                isSelected ? "text-accent-primary" : "text-content-muted"
              }`}
              {...({
                numberOfLines: 1,
                adjustsFontSizeToFit: true,
                minimumFontScale: 0.7,
              } as Record<string, unknown>)}
            >
              {option.label}
            </Text>
          </Pressable>
        );
      })}
    </Stack>
    {hasMore ? (
      <Stack className="items-center self-stretch">
        <Pressable
          onPress={onToggleExpanded}
          className="h-8 items-center justify-center rounded-none px-3"
        >
          <Text
            variant="unstyled"
            className="font-medium text-caption text-accent-primary"
          >
            {isExpanded ? "Show less" : "Show more"}
          </Text>
        </Pressable>
      </Stack>
    ) : null}
  </Stack>
);

RoleChipGridView.displayName = "RoleChipGridView";

export default memo(RoleChipGridView);
